import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import SearchBar from "../../common/SearchBar/SearchBar";
import Logo from "../../shared/Logo";
import { SearchResult } from "../TopBar/useGlobalSearch";
import { useChartsWatchlist } from "../Charts/hooks/useChartsWatchlist";
import { useMembership } from "../../../contexts/MembershipContext";
import { MOBILE_TOPBAR_HEIGHT } from "../../../contexts/SidebarContext";
import { isNative } from "../../../lib/platform";
import { getSymbolImageUrl } from "../../../lib/symbolImage";
import { SERIF_DISPLAY } from "../../Chat/ChatHero";
import { heroRise } from "../../Chat/chatMotion";
import {
  NativeLandingContainer,
  NativeLandingHeader,
  NativeListRow,
  NativeSectionLabel,
} from "../../Native/NativeLanding";
import { Stack } from "@mui/material";

/** Matches the chat/research heroes so the tile rows line up across tabs. */
const COMPARE_TILE_LIMIT = 6;

interface ComparisonLandingProps {
  onSelectStock: (ticker: string) => void;
  onSelectRight?: (ticker: string) => void;
}

const popularStocks = [
  { symbol: "AAPL", name: "Apple", logo: getSymbolImageUrl("AAPL") },
  { symbol: "NVDA", name: "Nvidia", logo: getSymbolImageUrl("NVDA") },
  { symbol: "MSFT", name: "Microsoft", logo: getSymbolImageUrl("MSFT") },
  { symbol: "GOOGL", name: "Alphabet", logo: getSymbolImageUrl("GOOGL") },
  { symbol: "AMZN", name: "Amazon", logo: getSymbolImageUrl("AMZN") },
];

const FREE_TIER_STOCKS = popularStocks;

const ComparisonLanding = ({ onSelectStock, onSelectRight }: ComparisonLandingProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isMembershipActive } = useMembership();

  const {
    data: chartsWatchlistData,
    isLoading: isChartsWatchlistLoading,
    error: chartsWatchlistError,
  } = useChartsWatchlist();

  const handleSelectItem = (item: SearchResult) => {
    onSelectStock(item.symbol);
  };

  const handleFreeTierStockClick = (symbol: string) => {
    onSelectStock(symbol);
    const others = FREE_TIER_STOCKS.filter((s) => s.symbol !== symbol);
    const random = others[Math.floor(Math.random() * others.length)];
    onSelectRight?.(random.symbol);
  };

  const hasWatchlistData =
    !chartsWatchlistError &&
    chartsWatchlistData?.data &&
    chartsWatchlistData.data.length > 0;

  const stocksToShow = !isMembershipActive
    ? FREE_TIER_STOCKS
    : hasWatchlistData
      ? chartsWatchlistData.data.map((item) => ({
          symbol: item.ticker,
          name: item.companyName,
          logo: item.logo,
        }))
      : popularStocks;

  const sectionTitle = !isMembershipActive
    ? "Try with these stocks"
    : hasWatchlistData
      ? "From your watchlist"
      : "Popular Stocks";


  // Native app: compact left-aligned header (no logo), search, and dense
  // company rows — vertically centred between the app bars.
  if (isNative()) {
    return (
      <NativeLandingContainer>
        <NativeLandingHeader
          title="Compare Stocks"
          subtitle="See any two companies side by side"
        />

        <SearchBar
          placeholder="Search any stock to start comparing..."
          width="100%"
          variant="chatgpt"
          onSelectItem={handleSelectItem}
          disabled={!isMembershipActive}
        />

        <NativeSectionLabel>{sectionTitle}</NativeSectionLabel>

        {isChartsWatchlistLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ color: "#22c55e" }} />
          </Box>
        )}
        {!isChartsWatchlistLoading && (
          <Stack spacing={1.25}>
            {stocksToShow.map((stock) => (
              <NativeListRow
                key={stock.symbol}
                leading={
                  <Logo src={stock.logo} alt={`${stock.name} logo`} ticker={stock.symbol} size={28} />
                }
                title={stock.name}
                subtitle={stock.symbol}
                onClick={() =>
                  isMembershipActive
                    ? onSelectStock(stock.symbol)
                    : handleFreeTierStockClick(stock.symbol)
                }
              />
            ))}
          </Stack>
        )}
      </NativeLandingContainer>
    );
  }

  // Web + mobile web: mirrors the chat and research heroes — same serif
  // heading, viewport math, entrance stagger, and logo-tile row — so all three
  // tabs read as one product.
  return (
    <Box
      sx={{
        height: { xs: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px)`, lg: "100vh" },
        overflowY: "auto",
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          width: "100%",
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ mb: 3, ...heroRise(0) }}>
          <img
            src={isDark ? "/insightLongLogoPrimaryWhiteText.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            style={{ height: 48, width: "auto", maxWidth: "100%" }}
          />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF_DISPLAY,
            fontWeight: 500,
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            letterSpacing: "-0.01em",
            lineHeight: 1.25,
            color: isDark ? "#ffffff" : "#1d1d1f",
            mb: { xs: 3.5, md: 4.5 },
            ...heroRise(0.05),
          }}
        >
          What would you like to compare?
        </Typography>

        <Box sx={{ width: "100%", ...heroRise(0.14) }}>
          <Tooltip title={!isMembershipActive ? "Available with Insight Pro" : ""} arrow>
            <span style={{ display: "block", width: "100%" }}>
              <SearchBar
                placeholder="Search any stock to start comparing..."
                width="100%"
                variant="chatgpt"
                onSelectItem={handleSelectItem}
                disabled={!isMembershipActive}
              />
            </span>
          </Tooltip>
        </Box>

        {/* Compact company tiles, identical to the chat/research hero rows:
            logo only, names in tooltips. Free tier keeps them clickable — a
            tap loads a sample comparison. */}
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {stocksToShow.slice(0, COMPARE_TILE_LIMIT).map((stock, index) => (
            <Tooltip key={stock.symbol} title={`Compare ${stock.name}`} arrow>
              <Box
                component="button"
                aria-label={`Compare ${stock.name}`}
                onClick={() =>
                  isMembershipActive
                    ? onSelectStock(stock.symbol)
                    : handleFreeTierStockClick(stock.symbol)
                }
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  p: 0,
                  cursor: "pointer",
                  borderRadius: 2,
                  border: isDark
                    ? "1px solid rgba(255, 255, 255, 0.1)"
                    : "1px solid rgba(0, 0, 0, 0.1)",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
                  transition: "border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
                    borderColor: isDark ? "rgba(34, 197, 94, 0.45)" : "rgba(34, 197, 94, 0.4)",
                    transform: "scale(1.05)",
                  },
                  "&:active": { transform: "scale(0.97)" },
                  "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
                  ...heroRise(0.2 + index * 0.045),
                }}
              >
                <Logo src={stock.logo} alt={stock.name} ticker={stock.symbol} size={22} />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ComparisonLanding;
